import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { CoachState } from "@/providers/walter-coach";
import { CoachActionType, CoachSuggestionPayload } from "@/services/walter-assistant";
import { ChevronDown, Compass, GripHorizontal, Loader2, SquareArrowOutUpRight, X } from "lucide-react";
import { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { resolveCoachEventAccent } from "./live-coach-signal.utils";
import { COACH_SUGGESTIONS_DRAWER_WIDTH, useDrawerLayout } from "./use-drawer-layout";

const L2_ACTION_LABELS: Record<CoachActionType, string> = {
    deepen_discovery: "Reprendre la découverte",
    reframe_value: "Repositionner la valeur",
    slow_down: "Ralentir",
    let_prospect_talk: "Laisser parler",
    summarize_and_confirm: "Résumer & valider",
    propose_next_step: "Proposer la suite",
    address_emerging_risk: "Anticiper un risque",
    recover_handoff: "Reprendre la main"
};

function formatCoachSuggestionAge(timestampMs: number, serverTime: string | null): string {
    const reference = serverTime ? new Date(serverTime).getTime() : Date.now();
    const diffMinutes = Math.max(0, Math.floor((reference - timestampMs) / 60000));
    if (diffMinutes < 1) return "À l'instant";
    if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
    return new Date(timestampMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatSimilarityScore(score: number | null | undefined): string | null {
    if (typeof score !== "number" || Number.isNaN(score)) return null;
    return `${Math.round(score * 100)}% de match`;
}

function resolveSuggestionAccentKey(suggestion: CoachSuggestionPayload): string | undefined {
    if (suggestion.detected_signal?.code) return suggestion.detected_signal.code;
    if (suggestion.source === "layer_2_conductor") return "layer_2_conductor";
    if (suggestion.source === "manual") return "manual";
    return suggestion.suggestion_code ?? undefined;
}

type CoachSuggestionsViewProps = {
    suggestions: CoachSuggestionPayload[];
    serverTime: string | null;
    isLoading: boolean;
    errorText: string | null;
    coachState: CoachState;
};

function CoachSuggestionsView({ suggestions, serverTime, isLoading, errorText, coachState }: CoachSuggestionsViewProps) {
    const isInCall = coachState === "open_in_call";
    const showEmptyOnCall = !isLoading && isInCall && suggestions.length === 0;

    const articleClass = "flex flex-col gap-3 rounded-xl bg-white p-5 mx-2";
    const chipClass = "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium";
    const similarityClass = "rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700";
    const ageClass = "shrink-0 pt-0.5 text-xs text-gray-400";
    const matchedQuoteClass = "border-l-2 border-gray-200 pl-4 text-base italic leading-snug text-gray-500";
    const verbatimClass = "text-2xl font-semibold leading-snug text-gray-900";
    const adviceClass = "text-2xl font-medium italic leading-snug text-gray-900";
    const handoffClass = "rounded-md border-l-2 border-indigo-300 bg-indigo-50/60 px-3 py-2 text-base leading-snug text-indigo-900";
    const handoffLabelClass = "mr-1 text-xs font-semibold uppercase tracking-wider text-indigo-500";

    return (
        <div className="flex flex-col gap-3 rounded-lg">
            {errorText ? <div className="rounded-lg border border-red-100 bg-red-50 px-2 py-1.5 text-[11px] text-red-700">{errorText}</div> : null}

            {isLoading && suggestions.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4" aria-busy="true" aria-live="polite">
                    <div className="flex flex-col gap-2">
                        <div className="h-3 w-48 animate-pulse rounded bg-gray-200" />
                        <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
                        <div className="h-3 w-4/5 animate-pulse rounded bg-gray-100" />
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground">Chargement en cours…</p>
                </div>
            ) : null}

            {showEmptyOnCall ? (
                <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                    <p className="text-sm font-normal text-muted-foreground">Les éléments apparaîtront au fil de l&apos;appel.</p>
                </div>
            ) : null}

            {suggestions.map((suggestion, index) => {
                const isTop = index === 0;
                const isL2 = suggestion.source === "layer_2_conductor";
                const isManual = suggestion.source === "manual";
                const isStreaming = Boolean(suggestion.isStreaming);
                const accentKey = resolveSuggestionAccentKey(suggestion);
                const accent = resolveCoachEventAccent(accentKey);

                const articleStyle: CSSProperties | undefined = isTop && accent ? ({ "--coach-suggestion-glow-color": accent.glow } as CSSProperties) : undefined;
                const signalChipStyle: CSSProperties | undefined = accent ? { color: accent.stroke, backgroundColor: `color-mix(in srgb, ${accent.glow}, transparent 55%)` } : undefined;

                const detected = suggestion.detected_signal;
                const actionLabel = suggestion.action_type ? L2_ACTION_LABELS[suggestion.action_type] : null;
                const chipLabel = isL2 ? (actionLabel ?? "Conseil pilote") : isManual ? "Générée manuellement" : (detected?.label ?? suggestion.suggestion_code ?? "Suggestion");
                const similarity = formatSimilarityScore(detected?.similarity_score);
                const matchedQuote = !isL2 && !isManual ? (detected?.matched_phrasing ?? detected?.evidence ?? suggestion.evidence ?? null) : null;

                return (
                    <article key={suggestion.suggestion_id} style={articleStyle} className={cn(articleClass, isTop ? "animate-coach-suggestion-glow" : "opacity-30")}>
                        <header className="flex items-start justify-between gap-2">
                            <div className="flex min-w-0 flex-wrap items-center gap-1">
                                <span className={cn(chipClass, isL2 ? "bg-indigo-600 text-white" : accent ? null : "bg-gray-100 text-gray-600")} style={isL2 ? undefined : signalChipStyle}>
                                    {isL2 ? <Compass className="size-4" aria-hidden /> : null}
                                    {chipLabel}
                                </span>
                                {similarity ? <span className={similarityClass}>{similarity}</span> : null}
                            </div>
                            <span className={ageClass}>{formatCoachSuggestionAge(suggestion.timestamp_ms, serverTime)}</span>
                        </header>

                        {matchedQuote ? <blockquote className={matchedQuoteClass}>«&nbsp;{matchedQuote}&nbsp;»</blockquote> : null}

                        {isL2 ? (
                            <p className={adviceClass}>{suggestion.suggested_answer}</p>
                        ) : (
                            <p className={verbatimClass}>
                                {isStreaming && !suggestion.suggested_answer ? (
                                    <Loader2 className="inline size-5 animate-spin text-gray-400" aria-hidden />
                                ) : (
                                    <>
                                        Répondez&nbsp;: «&nbsp;{suggestion.suggested_answer}
                                        {isStreaming ? <span className="animate-pulse text-primary">▍</span> : null}
                                        &nbsp;»
                                    </>
                                )}
                            </p>
                        )}

                        {isL2 && suggestion.example_handoff_phrase ? (
                            <blockquote className={handoffClass}>
                                <span className={handoffLabelClass}>Par exemple</span>
                                «&nbsp;{suggestion.example_handoff_phrase}&nbsp;»
                            </blockquote>
                        ) : null}
                    </article>
                );
            })}
        </div>
    );
}

type Props = {
    isOpen: boolean;
    onClose: () => void;
    suggestions: CoachSuggestionPayload[];
    serverTime: string | null;
    isLoading: boolean;
    errorText: string | null;
    coachState: CoachState;
    playbookName: string | null | undefined;
    /** URL de la fiche catalogue du thème courant (construit depuis le panier). */
    topThemeUrl?: string | null;
};

export function CoachSuggestionsDrawer({
    isOpen,
    onClose,
    suggestions,
    serverTime,
    isLoading,
    errorText,
    coachState,
    playbookName,
    topThemeUrl
}: Props) {
    const { height, left, isCollapsed, isDragging, toggleCollapsed, onLayoutHandlePointerDown } = useDrawerLayout();

    if (!isOpen) return null;

    return createPortal(
        <div
            className={cn(
                "fixed bottom-11 z-40 flex flex-col overflow-hidden rounded-t-md border border-b-0 bg-white shadow-lg",
                !isDragging && "transition-[height] duration-200 ease-out"
            )}
            style={{ height: isCollapsed ? 76 : height, left, width: COACH_SUGGESTIONS_DRAWER_WIDTH }}
            role="region"
            aria-label="Conseils du coach live"
        >
            <div
                className="flex shrink-0 cursor-move touch-none items-center justify-center border-b bg-gray-50 py-1.5"
                onPointerDown={onLayoutHandlePointerDown}
                aria-label="Déplacer et redimensionner le panneau des conseils"
            >
                <GripHorizontal className="size-4 text-gray-400" aria-hidden />
            </div>

            <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900">Conseils</p>
                    <p className="truncate text-xs text-gray-500">Playbook : {playbookName || "Pas de playbook"}</p>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                    {topThemeUrl ? (
                        <Button variant="ghost" size="icon" className="size-7" asChild aria-label="Ouvrir la fiche catalogue">
                            <Link to={topThemeUrl}>
                                <SquareArrowOutUpRight className="size-3.5" aria-hidden />
                            </Link>
                        </Button>
                    ) : null}
                    <Button
                        variant="ghost" size="icon" className="size-7"
                        onClick={toggleCollapsed}
                        aria-label={isCollapsed ? "Développer" : "Réduire"}
                    >
                        <ChevronDown className={`size-3.5 transition-transform duration-200 ${isCollapsed ? "" : "rotate-180"}`} aria-hidden />
                    </Button>
                    <Button variant="ghost" size="icon" className="size-7" onClick={onClose} aria-label="Fermer les conseils">
                        <X className="size-3.5" aria-hidden />
                    </Button>
                </div>
            </div>

            {/* contenu — toujours rendu, masqué par overflow-hidden sur le conteneur */}
            <ScrollArea className="min-h-0 flex-1">
                <div className="flex flex-col gap-4 bg-gray-50 p-4">
                        <CoachSuggestionsView suggestions={suggestions} serverTime={serverTime} isLoading={isLoading} errorText={errorText} coachState={coachState} />
                    </div>
            </ScrollArea>
        </div>,
        document.body
    );
}
